import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Info, Plus, X, AlertCircle, ChevronDown, ChevronUp, CheckCircle, Clock, Video, Link, Package, Sparkles, Lock } from 'lucide-react';
import ImageUploadPreviewEnhanced from '../ImageUploadPreviewEnhanced';
import VariantManagerV2 from '../VariantManagerV2';
import VariantEditorModal from '../VariantEditorModal';
import AttributeRenderer from '../AttributeRenderer';
import TagInput from '../TagInput';
import ProductOptionsConfig from '../ProductOptionsConfig';
import CustomOptionsConfig from '../CustomOptionsConfig';
import ProductOptionsModal from '../ProductOptionsModal';
import CustomOptionsModal from '../CustomOptionsModal';
import AvailabilityScheduler from '../AvailabilityScheduler';
import HolidayCalendar from '../HolidayCalendar';
import MasterCatalogSelector from '../MasterCatalogSelector';
import VendorRentalCalendar from './VendorRentalCalendar';
import CancellationPolicyBuilder from './CancellationPolicyBuilder';
import CategorySpecificAttributes from './CategorySpecificAttributes';
import GstAutoSuggestion from '../GstAutoSuggestion';
import GoogleCalendarSyncCard from './GoogleCalendarSyncCard';
import ServiceableAreaCard from './ServiceableAreaCard';
import ListingSearchInput from './ListingSearchInput';
import VolumetricBudgetMeter from '../VolumetricBudgetMeter';
import ComplianceCard from './ComplianceCard';
import CatalogItemsCard from './CatalogItemsCard';
import RoomTypesCard from './RoomTypesCard';
import HotelPropertyDetailsCard from './HotelPropertyDetailsCard';
import { useAuth } from '../../context/AuthContext';

const LISTING_TYPES_OPTIONS = [
  { value: 'service',      label: 'Service' },
  { value: 'rental',       label: 'Rental' },
  { value: 'product',      label: 'Product' },
  { value: 'event',        label: 'Event' },
  { value: 'subscription', label: 'Subscription' },
];

// ── Dedicated component so hooks run unconditionally ─────────────────────────
const TemplateSetupCard = ({ templateSetup, onTemplateSetupChange }) => {
  const { apiCall } = useAuth();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // Fetch categories whenever listing type changes
  useEffect(() => {
    if (!templateSetup.listingType) return;
    let cancelled = false;
    apiCall(`/api/categories/?listing_type=${templateSetup.listingType}&is_active=true`)
      .then(data => {
        if (cancelled) return;
        const sorted = (data || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setCategories(sorted);
        // Keep subcategories if category still valid
        const cat = sorted.find(c => c.name === templateSetup.category);
        setSubcategories((cat?.subcategories || []).sort((a, b) => (a.sort_order||0)-(b.sort_order||0)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [templateSetup.listingType]);

  // Update subcategories when category changes
  useEffect(() => {
    const cat = categories.find(c => c.name === templateSetup.category);
    setSubcategories((cat?.subcategories || []).sort((a, b) => (a.sort_order||0)-(b.sort_order||0)));
  }, [templateSetup.category, categories]);

  const sel = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white disabled:opacity-50";

  return (
    <Card className="border-2 border-indigo-200 bg-indigo-50/40">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="h-5 w-5 text-indigo-600" />
          Template Setup
        </CardTitle>
        <CardDescription>
          Define this template's identity. Vendors will see it when browsing templates.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Listing Type */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">
              Listing Type <span className="text-red-500">*</span>
            </label>
            <select
              value={templateSetup.listingType || 'service'}
              onChange={e => {
                onTemplateSetupChange?.('listingType', e.target.value);
                onTemplateSetupChange?.('category', '');
                onTemplateSetupChange?.('subcategory', '');
              }}
              className={sel}
              data-testid="template-listing-type"
            >
              {LISTING_TYPES_OPTIONS.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Template Name */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Yoga Trainer Default"
              value={templateSetup.templateName || ''}
              onChange={e => onTemplateSetupChange?.('templateName', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="template-name-input"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Category</label>
            <select
              value={templateSetup.category || ''}
              onChange={e => {
                onTemplateSetupChange?.('category', e.target.value);
                onTemplateSetupChange?.('subcategory', '');
              }}
              className={sel}
              data-testid="template-category"
            >
              <option value="">Select Category...</option>
              {categories.map(c => (
                <option key={c._id || c.name} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Subcategory */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Subcategory</label>
            <select
              value={templateSetup.subcategory || ''}
              onChange={e => onTemplateSetupChange?.('subcategory', e.target.value)}
              disabled={!templateSetup.category || subcategories.length === 0}
              className={sel}
              data-testid="template-subcategory"
            >
              <option value="">Select Subcategory...</option>
              {subcategories.map(s => (
                <option key={s._id || s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Description</label>
            <textarea
              rows={2}
              placeholder="Brief description of this template for vendors..."
              value={templateSetup.templateDescription || ''}
              onChange={e => onTemplateSetupChange?.('templateDescription', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              data-testid="template-description-input"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1.5">
              Priority <span className="text-xs text-gray-400">(1 = highest, shown first)</span>
            </label>
            <input
              type="number"
              min={1} max={99}
              value={templateSetup.templatePriority || 1}
              onChange={e => onTemplateSetupChange?.('templatePriority', parseInt(e.target.value) || 1)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="template-priority-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
import { useCategories } from '../../hooks/useCategories';
import { getPriceLabels } from '../../config/priceLabels';

// Feature flag for modal-based editing (can be controlled via props or config)
const USE_MODAL_EDITORS = true;

/**
 * Evaluate conditional visibility for a field
 */
const evaluateCondition = (condition, formData) => {
  if (condition === undefined || condition === null) return true;
  if (typeof condition === 'boolean') return condition;
  
  // String: check if field is truthy
  if (typeof condition === 'string') {
    // Handle nested path like "service_guarantee.enabled"
    const parts = condition.split('.');
    let value = formData;
    for (const part of parts) {
      value = value?.[part];
    }
    return !!value;
  }
  
  // Object: check field values
  if (typeof condition === 'object') {
    return Object.entries(condition).every(([field, expectedValue]) => {
      // Handle nested paths
      const parts = field.split('.');
      let actualValue = formData;
      for (const part of parts) {
        actualValue = actualValue?.[part];
      }
      
      if (Array.isArray(expectedValue)) {
        // Backward compat: if the condition field is not yet set on this listing,
        // show the field (handles listings created before template system existed)
        if (actualValue === undefined) return true;
        return expectedValue.includes(actualValue);
      }
      return actualValue === expectedValue;
    });
  }
  
  return true;
};

/**
 * Render a single form field based on its configuration
 */
const renderField = (field, formData, errors, handleChange, categories, subcategories, isSidebar = false, user = null, schema = null) => {
  const {
    name,
    label,
    labelIfVariants,
    type,
    placeholder,
    helperText,
    helperTextIfVariants,
    required,
    options,
    showIf,
    dependsOn,
    defaultValue,
    maxLength,
    showCharCount,
    rows,
    suffix,
    min,
    max,
    folder,
    tagType,
    hideIfVariants,
    disableIfVariants,
    section,
  } = field;

  // Check conditional visibility
  if (showIf && !evaluateCondition(showIf, formData)) {
    return null;
  }
  
  // Hide if variants are enabled (for inventory field)
  if (hideIfVariants && formData.hasVariants) {
    return null;
  }

  // Get contextual price labels based on subcategory
  const subcategoryId = formData.subcategoryId || formData.subcategory_id || formData.subcategory;
  const listingType = schema?.listingType || formData.listing_type || 'product';
  const priceLabelsConfig = getPriceLabels(subcategoryId, listingType);
  
  // Override labels for price-related fields
  let contextualLabel = label;
  let contextualHelper = helperText;
  
  if (name === 'price' || name === 'basePrice') {
    contextualLabel = priceLabelsConfig.price;
    contextualHelper = priceLabelsConfig.priceHelper;
  } else if (name === 'comparePrice' || name === 'compareAtPrice' || name === 'compare_at_price') {
    contextualLabel = priceLabelsConfig.comparePrice;
    contextualHelper = priceLabelsConfig.comparePriceHelper;
  }

  // Determine actual label and helper text based on variants state
  const actualLabel = (formData.hasVariants && labelIfVariants) ? labelIfVariants : contextualLabel;
  const actualHelperText = (formData.hasVariants && helperTextIfVariants) ? helperTextIfVariants : contextualHelper;

  // Dynamic label override (e.g., unit_price label changes with unit_type)
  let displayLabel = actualLabel;
  if (field.dynamicLabel) {
    const depValue = formData[field.dynamicLabel.field];
    if (depValue && field.dynamicLabel.values?.[depValue]) {
      displayLabel = field.dynamicLabel.values[depValue];
    }
  }

  // Get nested value for paths like "service_guarantee.enabled"
  const getValue = (fieldName) => {
    const parts = fieldName.split('.');
    let value = formData;
    for (const part of parts) {
      value = value?.[part];
    }
    return value ?? defaultValue ?? '';
  };
  
  // Set nested value
  const setValue = (fieldName, value) => {
    const parts = fieldName.split('.');
    if (parts.length === 1) {
      handleChange(fieldName, value);
    } else {
      // Handle nested updates
      const rootField = parts[0];
      const currentRoot = formData[rootField] || {};
      const newRoot = { ...currentRoot };
      let target = newRoot;
      for (let i = 1; i < parts.length - 1; i++) {
        target[parts[i]] = target[parts[i]] || {};
        target = target[parts[i]];
      }
      target[parts[parts.length - 1]] = value;
      handleChange(rootField, newRoot);
    }
  };

  const value = getValue(name);
  const error = errors[name];
  const isRequired = typeof required === 'function' ? required(formData) : required;
  const isDisabled = disableIfVariants && formData.hasVariants;
  
  // Common error border class
  const errorBorderClass = error ? 'border-red-500 ring-1 ring-red-500' : '';

  // Label component with error indicator - uses displayLabel for dynamic/variant-aware labeling
  const FieldLabel = () => (
    <Label htmlFor={name} className={`text-sm font-medium ${isSidebar ? 'text-gray-700' : ''} ${error ? 'text-red-600' : ''}`}>
      {displayLabel}
      {isRequired && <span className="text-red-500 ml-1">*</span>}
    </Label>
  );

  // Helper function to safely render helper text - uses actualHelperText for variant-aware text
  const renderHelperText = (helpText, fieldValue) => {
    // Use actualHelperText if no specific helpText is passed
    const textToRender = helpText || actualHelperText;
    if (!textToRender) return null;
    if (typeof helpText === 'string') return helpText;
    if (typeof helpText === 'object' && fieldValue) {
      return helpText[fieldValue] || null;
    }
    return null;
  };

  switch (type) {
    case 'row':
      // Render multiple fields in a horizontal row
      const rowFields = field.fields || [];
      return (
        <div key={name} className="grid grid-cols-12 gap-3">
          {rowFields.map((subField) => {
            // Determine column width
            let colSpan = 6; // Default to half
            if (subField.width === 'third') colSpan = 4;
            else if (subField.width === 'quarter') colSpan = 3;
            else if (subField.width === 'half') colSpan = 6;
            else if (subField.width === 'full') colSpan = 12;
            else if (typeof subField.width === 'number') colSpan = Math.round((subField.width / 100) * 12);
            else if (subField.width === '70') colSpan = 8;
            else if (subField.width === '30') colSpan = 4;
            
            // Check subfield visibility
            if (subField.showIf && !evaluateCondition(subField.showIf, formData)) {
              return null;
            }
            
            return (
              <div key={subField.name} className={`col-span-${colSpan}`} style={{ gridColumn: `span ${colSpan}` }}>
                {renderField(subField, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema)}
              </div>
            );
          })}
        </div>
      );

    case 'text':
    case 'email':
    case 'tel':
    case 'url':
    case 'number':
    case 'date':
    case 'time':
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <div className="relative">
            <Input
              id={name}
              type={type}
              value={value ?? ''}
              onChange={(e) => {
                const newValue = e.target.value;
                // For number inputs, parse as float when there's a valid number
                if (type === 'number') {
                  // Allow empty string for clearing the field
                  if (newValue === '' || newValue === null || newValue === undefined) {
                    setValue(name, '');
                  } else {
                    // Parse as number, fallback to string if NaN
                    const parsed = parseFloat(newValue);
                    setValue(name, isNaN(parsed) ? newValue : parsed);
                  }
                } else {
                  setValue(name, newValue);
                }
              }}
              placeholder={placeholder}
              maxLength={maxLength}
              min={min}
              max={max}
              disabled={isDisabled}
              className={`${errorBorderClass} ${suffix ? 'pr-12' : ''}`}
              data-testid={`field-${name}`}
            />
            {suffix && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                {suffix}
              </span>
            )}
          </div>
          {showCharCount && maxLength && (
            <p className="text-xs text-gray-500">{(value || '').toString().length}/{maxLength}</p>
          )}
          {renderHelperText(actualHelperText, value) && (
            <p className="text-xs text-gray-500">{renderHelperText(actualHelperText, value)}</p>
          )}
          {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <Textarea
            id={name}
            value={value}
            onChange={(e) => setValue(name, e.target.value)}
            placeholder={placeholder}
            rows={rows || 4}
            maxLength={maxLength}
            className={errorBorderClass}
            data-testid={`field-${name}`}
          />
          {showCharCount && maxLength && (
            <p className="text-xs text-gray-500">{(value || '').length}/{maxLength}</p>
          )}
          {helperText && typeof helperText === 'string' && <p className="text-xs text-gray-500">{helperText}</p>}
          {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p>}
        </div>
      );

    case 'select':
      // Dynamic options based on another field's value (e.g., unit_type changes with subscription_type)
      const selectOptions = (() => {
        if (field.optionsByField) {
          const depValue = formData[field.optionsByField.field];
          const mapped = depValue ? field.optionsByField.map?.[depValue] : null;
          return Array.isArray(mapped) ? mapped : (field.optionsByField.default || []);
        }
        return Array.isArray(options) ? options : [];
      })();
      // Handle helperText that could be an object keyed by value
      const getSelectHelperText = () => {
        if (!helperText) return null;
        if (typeof helperText === 'string') return helperText;
        if (typeof helperText === 'object' && value) {
          return helperText[value];
        }
        return null;
      };
      
      // Check if any option requires KYC and user is not admin
      // For admins, KYC is always bypassed
      const checkKycForOption = (opt) => {
        if (typeof opt !== 'object') return false;
        // Admins bypass KYC requirement
        if (user?.role === 'admin' || user?.role === 'super_admin') return false;
        return opt.requiresKyc === true;
      };
      
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <Select
            value={value?.toString()}
            onValueChange={(val) => {
              // Check if selected option requires KYC
              const selectedOpt = selectOptions.find(opt => 
                (typeof opt === 'object' ? opt.value : opt)?.toString() === val
              );
              if (checkKycForOption(selectedOpt)) {
                // Show toast error for non-admins trying to select KYC-required option
                console.warn('KYC required for this option');
                return; // Don't allow selection
              }
              setValue(name, val);
            }}
          >
            <SelectTrigger className={errorBorderClass} data-testid={`field-${name}`}>
              <SelectValue placeholder={placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {selectOptions.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                const isKycRequired = checkKycForOption(opt);
                
                return (
                  <SelectItem 
                    key={optValue} 
                    value={optValue?.toString()}
                    disabled={isKycRequired}
                  >
                    {optLabel} {isKycRequired && '🔒'}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {getSelectHelperText() && <p className="text-xs text-gray-500">{getSelectHelperText()}</p>}
          {error && <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p>}
        </div>
      );

    case 'category-select':
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <Select
            value={value}
            onValueChange={(val) => setValue(name, val)}
          >
            <SelectTrigger className={error ? 'border-red-500' : ''} data-testid={`field-${name}`}>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {(categories || []).map((cat) => (
                <SelectItem 
                  key={typeof cat === 'object' ? cat.value || cat.name : cat} 
                  value={typeof cat === 'object' ? cat.value || cat.name : cat}
                >
                  {typeof cat === 'object' ? cat.label || cat.name : cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      );

    case 'subcategory-select':
      if (!subcategories || subcategories.length === 0) {
        return null;
      }
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <Select
            value={value}
            onValueChange={(val) => setValue(name, val)}
          >
            <SelectTrigger data-testid={`field-${name}`}>
              <SelectValue placeholder="Select subcategory" />
            </SelectTrigger>
            <SelectContent>
              {subcategories.map((subcat) => (
                <SelectItem 
                  key={typeof subcat === 'object' ? subcat.value || subcat.name : subcat} 
                  value={typeof subcat === 'object' ? subcat.value || subcat.name : subcat}
                >
                  {typeof subcat === 'object' ? subcat.label || subcat.name : subcat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'checkbox':
      return (
        <div key={name} className="flex items-center space-x-2 py-2">
          <Checkbox
            id={name}
            checked={!!value}
            onCheckedChange={(checked) => setValue(name, checked)}
            data-testid={`field-${name}`}
          />
          <Label htmlFor={name} className="cursor-pointer text-sm">{label}</Label>
          {helperText && (
            <span className="text-xs text-gray-500 ml-2">({helperText})</span>
          )}
        </div>
      );

    case 'radio':
      const radioOptions = Array.isArray(options) ? options : [];
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <RadioGroup
            value={value?.toString()}
            onValueChange={(val) => {
              // Convert back to boolean if needed
              const parsedVal = val === 'true' ? true : val === 'false' ? false : val;
              setValue(name, parsedVal);
            }}
          >
            <div className="flex flex-wrap gap-4">
              {radioOptions.map((opt) => {
                const optValue = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <div key={optValue?.toString()} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={optValue?.toString()} 
                      id={`${name}-${optValue}`}
                      data-testid={`field-${name}-${optValue}`}
                    />
                    <Label htmlFor={`${name}-${optValue}`} className="cursor-pointer">
                      {optLabel}
                    </Label>
                  </div>
                );
              })}
            </div>
          </RadioGroup>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      );

    case 'radio-card':
      const cardOptions = Array.isArray(options) ? options : [];
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {cardOptions.map((opt) => {
              const optValue = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? opt.label : opt;
              const optDescription = typeof opt === 'object' ? opt.description : null;
              const isSelected = value === optValue;
              return (
                <label
                  key={optValue}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`field-${name}-${optValue}`}
                >
                  <input
                    type="radio"
                    name={name}
                    value={optValue}
                    checked={isSelected}
                    onChange={() => setValue(name, optValue)}
                    className="mt-0.5 w-4 h-4 text-blue-600"
                  />
                  <div>
                    <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                      {optLabel}
                    </span>
                    {optDescription && (
                      <p className="text-xs text-gray-500 mt-0.5">{optDescription}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      );

    case 'checkbox-group':
      // Multiple selection with checkboxes
      const checkboxGroupOptions = Array.isArray(options) ? options : [];
      const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
      
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <div className="space-y-2">
            {checkboxGroupOptions.map((opt) => {
              const optValue = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? opt.label : opt;
              const optDescription = typeof opt === 'object' ? opt.description : null;
              const isChecked = currentValues.includes(optValue);
              
              return (
                <label
                  key={optValue}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    isChecked 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => {
                      let newValues;
                      if (checked) {
                        newValues = [...currentValues, optValue];
                      } else {
                        newValues = currentValues.filter(v => v !== optValue);
                      }
                      setValue(name, newValues);
                    }}
                    className="mt-0.5"
                  />
                  <div>
                    <span className={`text-sm font-medium ${isChecked ? 'text-blue-700' : 'text-gray-700'}`}>
                      {optLabel}
                    </span>
                    {optDescription && (
                      <p className="text-xs text-gray-500 mt-0.5">{optDescription}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      );

    case 'media': {
      // Media upload with support for images, videos, 3D models
      // Includes direct upload, gallery picker, and video URL input
      const mediaLabel = label || 'Images / Videos / 3D Models';

      // ── FORMAT BRIDGE ──────────────────────────────────────────────────────────
      // CSV import stores media as objects: { id, type, url, cdn_url, alt, position }
      // ImageUploadPreviewEnhanced only handles plain string URLs.
      // We extract URLs here and reconstruct full objects on change, so that:
      //   • CSV-imported images appear in the editor grid
      //   • Removing / reordering works
      //   • Uploading a new image APPENDS (imported images are never lost)
      //   • Saved format is always consistent { id, type, url, cdn_url, alt, position }[]
      const rawMedia = value || [];
      const existingUrls = rawMedia
        .map(item => (typeof item === 'string' ? item : (item?.cdn_url || item?.url)))
        .filter(Boolean);

      // Build url → object lookup to preserve metadata (id, alt, type) on save
      const mediaByUrl = {};
      rawMedia.forEach(item => {
        if (item && typeof item === 'object') {
          const key = item.cdn_url || item.url;
          if (key) mediaByUrl[key] = item;
        }
      });

      const handleMediaChange = (urls) => {
        const mediaObjects = urls.map((url, idx) => {
          if (mediaByUrl[url]) {
            // Preserve existing metadata, just re-index position
            return { ...mediaByUrl[url], position: idx + 1 };
          }
          // Newly uploaded image — scaffold a minimal media object
          return {
            id: `media-${Date.now()}-${idx}`,
            type: 'image',
            url,
            cdn_url: url,
            alt: '',
            position: idx + 1,
          };
        });
        setValue(name, mediaObjects);
      };

      return (
        <div key={name} className="space-y-3">
          <FieldLabel label={mediaLabel} />
          <ImageUploadPreviewEnhanced
            onFilesChange={handleMediaChange}
            existingFiles={existingUrls}
            folder={folder}
          />
          
          {/* Video URL Input for YouTube/Vimeo */}
          <div className="border rounded-lg p-3 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <Video className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Add Video URL</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Paste YouTube or Vimeo URL..."
                value={formData[`${name}_videoUrl`] || ''}
                onChange={(e) => handleChange(`${name}_videoUrl`, e.target.value)}
                className="flex-1 text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const videoUrl = formData[`${name}_videoUrl`];
                  if (videoUrl && (videoUrl.includes('youtube') || videoUrl.includes('youtu.be') || videoUrl.includes('vimeo'))) {
                    const currentVideos = formData.videoUrls || [];
                    handleChange('videoUrls', [...currentVideos, videoUrl]);
                    handleChange(`${name}_videoUrl`, '');
                  }
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Supports YouTube and Vimeo links</p>
            
            {/* Display added video URLs */}
            {formData.videoUrls && formData.videoUrls.length > 0 && (
              <div className="mt-2 space-y-1">
                {formData.videoUrls.map((url, idx) => (
                  <div key={url.id || url.name || `${url.title || "url"}-${idx}`} className="flex items-center gap-2 text-sm bg-white p-2 rounded border">
                    <Link className="h-3 w-3 text-blue-500" />
                    <span className="flex-1 truncate text-gray-600">{url}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newUrls = formData.videoUrls.filter((_, i) => i !== idx);
                        handleChange('videoUrls', newUrls);
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      );
    } // end case 'media'

    case 'badge-select':
      const badgeOptions = Array.isArray(options) ? options : [];
      const selectedBadges = Array.isArray(value) ? value : [];
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <div className="flex flex-wrap gap-2" data-testid={`field-${name}`}>
            {badgeOptions.map((opt) => (
              <Badge
                key={opt}
                variant={selectedBadges.includes(opt) ? 'default' : 'outline'}
                className="cursor-pointer transition-all"
                onClick={() => {
                  const newValues = selectedBadges.includes(opt)
                    ? selectedBadges.filter((v) => v !== opt)
                    : [...selectedBadges, opt];
                  setValue(name, newValues);
                }}
                data-testid={`badge-${name}-${opt}`}
              >
                {opt}
              </Badge>
            ))}
          </div>
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
        </div>
      );

    case 'tag-input':
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <TagInput
            value={value || []}
            onChange={(tags) => setValue(name, tags)}
            tagType={tagType}
            placeholder={placeholder}
          />
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
        </div>
      );

    case 'listing-search':
      return (
        <div key={name} className="space-y-2">
          <FieldLabel />
          <ListingSearchInput
            value={value || []}
            onChange={(items) => setValue(name, items)}
            placeholder={placeholder}
            currentListingId={formData.id}
            maxItems={field.maxItems}
          />
          {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
        </div>
      );

    case 'info': {
      // Dynamic GST info block — reads from vendor's compliance tier
      if (field.dynamic) {
        const complianceTier = user?.vendor_profile?.business_details?.tax_compliance_tier
          || user?.compliance_tier || '';
        const state = user?.vendor_profile?.business_details?.state
          || user?.vendor_profile?.business_details?.registered_state || '';

        let tierText = '';
        let colorKey = 'gray';

        if (complianceTier.includes('tier_3') || complianceTier.includes('full_gst') || complianceTier.includes('gst_iec')) {
          tierText = 'Tier 3 — Sellable across India (Pan-India GST Registered)';
          colorKey = 'green';
        } else if (complianceTier.includes('tier_2') || complianceTier.includes('gst')) {
          tierText = `Tier 2 — Sellable only within ${state || 'your registered state'} (Intra-State only)`;
          colorKey = 'amber';
        } else if (complianceTier) {
          tierText = 'Tier 1 — Basic compliance. Upgrade your plan for expanded selling rights.';
          colorKey = 'blue';
        } else {
          tierText = 'GST setup incomplete. Please complete your GST profile to enable tax compliance.';
          colorKey = 'gray';
        }

        const colorMap = {
          green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: 'text-green-600' },
          amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: 'text-amber-600' },
          blue:  { bg: 'bg-blue-50',  border: 'border-blue-200',  text: 'text-blue-700',  icon: 'text-blue-600'  },
          gray:  { bg: 'bg-gray-50',  border: 'border-gray-200',  text: 'text-gray-500',  icon: 'text-gray-400'  },
        };
        const cls = colorMap[colorKey] || colorMap.gray;

        return (
          <div key={name} className={`p-3 rounded-lg border ${cls.bg} ${cls.border}`}>
            <div className="flex items-start gap-2">
              <Info className={`h-4 w-4 ${cls.icon} mt-0.5 flex-shrink-0`} />
              <div>
                <p className={`text-sm font-semibold ${cls.text}`}>{displayLabel}</p>
                <p className={`text-xs mt-0.5 ${cls.text}`}>{tierText}</p>
              </div>
            </div>
          </div>
        );
      }

      // Static info block (e.g., self_delivery_info)
      return (
        <div key={name} className="p-3 rounded-lg border bg-blue-50 border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">{field.content || displayLabel}</p>
          </div>
        </div>
      );
    }

    case 'number-with-unit': {
      // Composite: number input + unit select rendered side-by-side
      const unitOptions = field.unitOptions || [];
      const unitFieldName = field.unitField;
      const unitValue = formData[unitFieldName] ?? (unitOptions[0]?.value || '');
      return (
        <div className="space-y-2">
          <FieldLabel />
          <div className="flex gap-2">
            <Input
              id={name}
              type="number"
              min="1"
              placeholder={placeholder || 'e.g. 3'}
              value={value || ''}
              onChange={(e) => handleChange(name, e.target.value)}
              className={`flex-1 ${errorBorderClass}`}
              data-testid={`field-${name}`}
            />
            <Select
              value={unitValue}
              onValueChange={(v) => handleChange(unitFieldName, v)}
            >
              <SelectTrigger className="w-32" data-testid={`field-${unitFieldName}`}>
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent>
                {unitOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {field.helperText && <p className="text-xs text-gray-500">{field.helperText}</p>}
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }

    case 'section-header':
      return (
        <div className="pt-4 pb-1">
          <div className="border-l-4 border-blue-500 pl-3">
            <h4 className="font-semibold text-sm text-gray-900">{label}</h4>
            {field.description && (
              <p className="text-xs text-gray-500 mt-0.5">{field.description}</p>
            )}
          </div>
        </div>
      );

    case 'toggle': {
      const isToggleOn = value === true || value === 'true';
      return (
        <div className="flex items-center justify-between rounded-lg border p-3 bg-gray-50">
          <div className="space-y-0.5 flex-1 mr-4">
            <FieldLabel />
            {field.helperText && (
              <p className="text-xs text-gray-500">{field.helperText}</p>
            )}
          </div>
          <Switch
            id={name}
            checked={isToggleOn}
            onCheckedChange={(checked) => handleChange(name, checked)}
            data-testid={`field-${name}`}
          />
        </div>
      );
    }

    default:
      return null;
  }
};

/**
 * SchemaCardRenderer - Renders a card based on schema configuration
 */
const SchemaCardRenderer = ({
  cardId,
  schema,
  formData,
  errors,
  categories,
  subcategories,
  taxonomyAttributes,
  customWorkflow,
  handleChange,
  handleTaxonomyAttributeChange,
  isSidebar = false,
  // Template mode props
  isTemplateMode = false,
  templateSetup = {},
  onTemplateSetupChange,
  // Filter attributes injection
  insertBeforeTagInput = null,
  // Legal & Compliance card (iter-347)
  complianceConfig,
  requiredLicenseType,
  shelfLifeFloorDays,
  shelfLifeGuaranteeWording,
  defaultLicenses,
  listingId = null,
  onSaveDraftClick,
  saveDraftLoading = false,
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const cardConfig = schema.cards?.[cardId];
  
  // State for collapsible cards - must be declared before any early returns
  const isCollapsible = cardConfig?.collapsible === true;
  const [isCollapsed, setIsCollapsed] = useState(cardConfig?.collapsedByDefault === true);
  
  // State for Master Catalog modal
  const [showMasterCatalogModal, setShowMasterCatalogModal] = useState(false);
  
  // Handle GoogleCalendarSync with the dedicated component - check BEFORE other card logic
  if (cardId === 'googleCalendarSync') {
    // Check visibility condition first
    const calendarCardConfig = schema.cards?.[cardId];
    if (calendarCardConfig?.showIf && !evaluateCondition(calendarCardConfig.showIf, formData)) {
      return null;
    }
    return (
      <GoogleCalendarSyncCard
        formData={formData}
        handleChange={handleChange}
      />
    );
  }
  
  if (!cardConfig) {
    // Handle special card types
    // iter-362 — Products always show this card. Other listing types
    // (Service/Event/etc) only show it when the admin has activated
    // compliance for the resolved category/subcategory (Food/Wellness
    // FSSAI/CDSCO etc) — otherwise it stays hidden, matching the backend's
    // CMS-driven gate in utils/listing_compliance.py.
    const _complianceActivatedForCategory = !!(
      requiredLicenseType || complianceConfig?.enable_expiry_rules || complianceConfig?.enable_warranty_rules
    );
    if (cardId === 'compliance' && (schema?.listingType === 'product' || _complianceActivatedForCategory)) {
      return (
        <ComplianceCard
          formData={formData}
          handleChange={handleChange}
          complianceConfig={complianceConfig}
          requiredLicenseType={requiredLicenseType}
          shelfLifeFloorDays={shelfLifeFloorDays}
          shelfLifeGuaranteeWording={shelfLifeGuaranteeWording}
          defaultLicenses={defaultLicenses}
          errors={errors}
          isPhysicalProduct={schema?.listingType === 'product'}
          listingId={listingId}
          isAdmin={isAdmin}
        />
      );
    }

    // iter-386 — Universal Catalog Listing, Phase 1 foundation.
    if (cardId === 'catalogItems' && schema?.listingType === 'catalog') {
      return <CatalogItemsCard listingId={listingId} onSaveDraftClick={onSaveDraftClick} saveDraftLoading={saveDraftLoading} />;
    }

    // Hotel Booking Engine v8 (Workstream A) — RoomType/RoomRate manager,
    // only for a Rental listing explicitly marked `is_hotel` (v6 §1.1:
    // Hotel = Listing(listing_type="rental", data.is_hotel=true)). Hidden
    // entirely for ordinary (non-hotel) Rental listings — zero UI/behavior
    // change for them.
    if (cardId === 'roomTypes') {
      if (schema?.listingType === 'rental' && formData?.is_hotel) {
        return <RoomTypesCard listingId={listingId} onSaveDraftClick={onSaveDraftClick} saveDraftLoading={saveDraftLoading} />;
      }
      return null;
    }

    // v8 Round-3 — Property-level amenities/landmarks/class/location.
    // Plain listing-level `data` fields (unlike roomTypes, no sub-resource),
    // so it only needs formData/handleChange like other custom cards below.
    if (cardId === 'hotelPropertyDetails') {
      if (schema?.listingType === 'rental' && formData?.is_hotel) {
        return <HotelPropertyDetailsCard formData={formData} handleChange={handleChange} />;
      }
      return null;
    }

    if (cardId === 'taxonomyAttributes' && schema.features?.hasTaxonomy) {
      return (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Taxonomy Attributes</CardTitle>
          </CardHeader>
          <CardContent>
            <AttributeRenderer
              listingType={schema.listingType}
              categoryName={formData[schema.categoryField] || formData.category}
              subcategoryName={formData[schema.subcategoryField] || formData.subcategory}
              values={taxonomyAttributes}
              onChange={handleTaxonomyAttributeChange}
              showCard={false}
            />
          </CardContent>
        </Card>
      );
    }
    
    if (cardId === 'publishing') {
      return (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Publishing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status</span>
              <Badge
                data-testid="publishing-status-badge"
                className={
                  formData.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                  formData.status === 'inactive' ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' :
                  formData.status === 'rejected' ? 'bg-red-100 text-red-700 hover:bg-red-100' :
                  'bg-gray-100 text-gray-700 hover:bg-gray-100'
                }
              >
                {formData.status === 'inactive' ? 'Inactive' : (formData.status || 'draft')}
              </Badge>
            </div>
            {formData.revision_status && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Revision</span>
                <Badge variant="outline">{formData.revision_status}</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }
    
    if (cardId === 'listingStatus') {
      const getStatusConfig = (status) => {
        switch(status) {
          case 'active': return { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Active' };
          case 'pending': return { icon: Clock, color: 'bg-yellow-100 text-yellow-700', label: 'Pending Review' };
          case 'rejected': return { icon: X, color: 'bg-red-100 text-red-700', label: 'Rejected' };
          case 'inactive': return { icon: AlertCircle, color: 'bg-gray-100 text-gray-700', label: 'Inactive' };
          default: return { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'Draft' };
        }
      };
      const statusConfig = getStatusConfig(formData.status);
      const StatusIcon = statusConfig.icon;
      
      return (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Listing Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className={`flex items-center gap-2 p-3 rounded-lg ${statusConfig.color}`}>
              <StatusIcon className="h-4 w-4" />
              <span className="font-medium text-sm">{statusConfig.label}</span>
            </div>
            {formData.revision_status && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Revision Status</span>
                <Badge variant="outline" className="text-xs">{formData.revision_status}</Badge>
              </div>
            )}
            {formData.published_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Published</span>
                <span className="text-gray-900">{new Date(formData.published_at).toLocaleDateString()}</span>
              </div>
            )}
            {formData.updated_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Last Updated</span>
                <span className="text-gray-900">{new Date(formData.updated_at).toLocaleDateString()}</span>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }
    
    if (cardId === 'vendor') {
      return null; // Handled separately in parent
    }
    
    return null;
  }
  
  // Check card-level visibility
  if (cardConfig.showIf && !evaluateCondition(cardConfig.showIf, formData)) {
    return null;
  }

  // v8 — When a Rental listing is a Hotel, the generic flat Pricing card
  // is meaningless (each Room Type sets its own nightly rate via the
  // Pricing Engine). Instead of silently hiding the whole card (every
  // field inside has showIf: is_hotel:[false]), show it disabled with a
  // clear explanation so vendors aren't confused by a "missing" section.
  if (cardId === 'pricing' && schema?.listingType === 'rental' && formData?.is_hotel) {
    return (
      <Card className="border-dashed bg-gray-50" data-testid="pricing-card-disabled-hotel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-gray-500">
            <Lock className="h-4 w-4" />
            {cardConfig.title || 'Pricing & Tax'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2 opacity-60 pointer-events-none select-none">
            <Label className="text-sm font-medium text-gray-500">Pricing Model</Label>
            <Select value="room_type_based" disabled>
              <SelectTrigger className="bg-gray-100" data-testid="pricing-model-disabled-select">
                <SelectValue>Managed by Room Types</SelectValue>
              </SelectTrigger>
            </Select>
          </div>
          <div className="flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg p-3">
            <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-indigo-700">
              Pricing for this hotel listing is set per Room Type and resolved by the pricing
              engine (base / weekday / seasonal / festival rates). Add or edit nightly rates in
              the <strong>Room Types</strong> section above — the lowest active rate becomes this
              listing's starting price shown to customers.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Handle VariantManagerV2 component - Use modal or inline based on feature flag
  if (cardConfig.component === 'VariantManagerV2') {
    // Use modal-based editor for cleaner UX
    if (USE_MODAL_EDITORS) {
      return (
        <VariantEditorModal
          formData={formData}
          handleChange={handleChange}
          schema={schema}
          errors={errors}
          cardConfig={cardConfig}
          listingId={formData.id}
        />
      );
    }
    
    // Fallback: Original inline implementation
    const isCollapsed = cardConfig.collapsedByDefault && !formData.hasVariants;
    const listingTypeLabel = schema?.listingType === 'service' ? 'Service' : 
                              schema?.listingType === 'rental' ? 'Rental' : 'Product';
    const isService = schema?.listingType === 'service';
    
    return (
      <Card className={`transition-all duration-200 ${isCollapsed ? 'border-dashed' : ''}`}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                id="hasVariants"
                checked={!!formData.hasVariants}
                onCheckedChange={(checked) => handleChange('hasVariants', checked)}
                data-testid="toggle-variants"
              />
              <div>
                <Label htmlFor="hasVariants" className="cursor-pointer font-medium">
                  {cardConfig.title || `${listingTypeLabel} Variants`}
                </Label>
                {!formData.hasVariants && cardConfig.description && (
                  <p className="text-xs text-gray-500 mt-0.5">{cardConfig.description}</p>
                )}
              </div>
            </div>
            {formData.hasVariants && (
              <Badge variant="secondary" className="text-xs">Enabled</Badge>
            )}
          </div>
        </CardHeader>
        
        {/* Example Preview when collapsed */}
        {!formData.hasVariants && (
          <CardContent className="pt-0 pb-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-dashed border-gray-200">
              <p className="text-xs text-gray-400 mb-2">
                Example: {isService ? 'Duration' : 'Size'} options for customers
              </p>
              <div className="flex flex-wrap gap-2">
                {isService ? (
                  <>
                    <span className="px-3 py-1.5 text-sm border-2 border-gray-300 bg-white text-gray-600 rounded-lg">30 mins</span>
                    <span className="px-3 py-1.5 text-sm border-2 border-gray-300 bg-white text-gray-600 rounded-lg">60 mins</span>
                    <span className="px-3 py-1.5 text-sm border-2 border-gray-300 bg-white text-gray-600 rounded-lg">90 mins</span>
                  </>
                ) : (
                  <>
                    <span className="px-3 py-1.5 text-sm border-2 border-gray-300 bg-white text-gray-600 rounded-lg">9 inch</span>
                    <span className="px-3 py-1.5 text-sm border-2 border-gray-300 bg-white text-gray-600 rounded-lg">12 inch</span>
                    <span className="px-3 py-1.5 text-sm border-2 border-gray-300 bg-white text-gray-600 rounded-lg">16 inch</span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        )}
        
        {formData.hasVariants && (
          <CardContent className="pt-0 space-y-4">
            {/* Variant Validation Errors */}
            {Object.entries(errors).filter(([key]) => key.startsWith('variant') || key.startsWith('combo')).length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-1">Variant Issues:</p>
                    <ul className="space-y-0.5">
                      {Object.entries(errors)
                        .filter(([key]) => key.startsWith('variant') || key.startsWith('combo'))
                        .slice(0, 5)
                        .map(([key, msg]) => (
                          <li key={key}>• {msg}</li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Pricing Mode Toggle - Only show if vendor has manual pricing enabled */}
            {formData.vendorHasManualPricing && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                <div>
                  <p className="text-sm font-medium">Pricing Mode for this product</p>
                  <p className="text-xs text-gray-500">
                    {formData.manualPricingForProduct 
                      ? 'Manual: Final price will be confirmed offline' 
                      : 'Automatic: Prices update based on selections'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${!formData.manualPricingForProduct ? 'font-medium text-green-600' : 'text-gray-400'}`}>
                    Auto
                  </span>
                  <button
                    type="button"
                    onClick={() => handleChange('manualPricingForProduct', !formData.manualPricingForProduct)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.manualPricingForProduct ? 'bg-amber-500' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.manualPricingForProduct ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-xs ${formData.manualPricingForProduct ? 'font-medium text-amber-600' : 'text-gray-400'}`}>
                    Manual
                  </span>
                </div>
              </div>
            )}

            <VariantManagerV2
              variants={formData.variants || []}
              variantCombinations={formData.variantCombinations || []}
              onVariantsChange={(variants) => handleChange('variants', variants)}
              onCombinationsChange={(combinations) => handleChange('variantCombinations', combinations)}
              basePrice={parseFloat(
                // iter-221g — type-aware base-price waterfall. Subscriptions
                // store under base_plan_price/unit_price, Events under
                // ticketPrice, Rentals under dailyRate. Falling through
                // to formData.price for these types made the base variant
                // show ₹0 (the bug reported on the events listing form).
                schema?.listingType === 'Subscriptions' || schema?.listingType === 'subscription'
                  ? (formData.base_plan_price || formData.unit_price || formData.price)
                  : schema?.listingType === 'event'
                    ? (formData.ticketPrice || formData.price)
                    : schema?.listingType === 'rental'
                      ? (formData.dailyRate || formData.price)
                      : formData.price
              ) || 0}
              listingType={schema?.listingType || 'product'}
              config={cardConfig.config}
            />
          </CardContent>
        )}
      </Card>
    );
  }
  
  // Handle other special components
  if (cardConfig.component === 'AvailabilityScheduler') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{cardConfig.title || 'Availability Schedule'}</CardTitle>
          {cardConfig.description && (
            <CardDescription>{cardConfig.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <AvailabilityScheduler
            availability={formData.availabilitySettings || {}}
            onChange={(settings) => handleChange('availabilitySettings', settings)}
            serviceDuration={formData.duration}
            variants={formData.variants || []}
          />
        </CardContent>
      </Card>
    );
  }

  if (cardConfig.component === 'VendorRentalCalendar') {
    return (
      <VendorRentalCalendar
        formData={formData}
        handleChange={handleChange}
      />
    );
  }

  if (cardConfig.component === 'CancellationPolicyBuilder') {
    return (
      <CancellationPolicyBuilder
        formData={formData}
        handleChange={handleChange}
      />
    );
  }
  
  if (cardConfig.component === 'HolidayCalendar') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{cardConfig.title || 'Holiday Calendar'}</CardTitle>
          {cardConfig.description && (
            <CardDescription>{cardConfig.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <HolidayCalendar
            holidays={formData.holidays || []}
            onChange={(holidays) => handleChange('holidays', holidays)}
          />
        </CardContent>
      </Card>
    );
  }
  
  if (cardConfig.component === 'ProductOptionsConfig' || cardConfig.component === 'CustomOptionsConfig') {
    // Use modal-based editor for cleaner UX
    if (USE_MODAL_EDITORS) {
      return (
        <CustomOptionsModal
          formData={formData}
          handleChange={handleChange}
          schema={schema}
          cardConfig={cardConfig}
        />
      );
    }
    
    // Fallback: Original inline implementation
    const optionsLabel = 'Custom Options';
    
    return (
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            {cardConfig.title || optionsLabel}
          </CardTitle>
          {cardConfig.description && (
            <CardDescription>{cardConfig.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          <CustomOptionsConfig
            categoryId={formData.categoryId || formData.category_id}
            subcategoryId={formData.subcategoryId || formData.subcategory_id}
            listingId={formData.id}
            basePrice={formData.price || formData.basePrice || 0}
            onConfigChange={(config) => {
              handleChange('customOptionsConfig', config);
              handleChange('productOptionsConfig', config); // Backward compatibility
              // Auto-set hasCustomOptions based on whether options exist
              handleChange('hasCustomOptions', config?.hasOptions || false);
              handleChange('hasProductOptions', config?.hasOptions || false);
            }}
          />
        </CardContent>
      </Card>
    );
  }
  
  if (cardConfig.component === 'MasterCatalogSelector') {
    // ── Template mode: render the Template Setup card instead ────────────
    if (isTemplateMode) {
      const LISTING_TYPES = [
        { value: 'service',      label: 'Service' },
        { value: 'rental',       label: 'Rental' },
        { value: 'product',      label: 'Product' },
        { value: 'event',        label: 'Event' },
        { value: 'subscription', label: 'Subscription' },
      ];
      return (
        <Card className="border-2 border-indigo-200 bg-indigo-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              Template Setup
            </CardTitle>
            <CardDescription>
              Define this template's identity. Vendors will see it when browsing templates to start their listing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Listing Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={templateSetup.listingType || 'service'}
                  onChange={e => onTemplateSetupChange?.('listingType', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  data-testid="template-listing-type"
                >
                  {LISTING_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Yoga Trainer Default"
                  value={templateSetup.templateName || ''}
                  onChange={e => onTemplateSetupChange?.('templateName', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="template-name-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-700 block mb-1.5">Description</label>
                <textarea
                  rows={2}
                  placeholder="Brief description of this template for vendors..."
                  value={templateSetup.templateDescription || ''}
                  onChange={e => onTemplateSetupChange?.('templateDescription', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  data-testid="template-description-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1.5">
                  Priority <span className="text-xs text-gray-400">(1 = highest, shown first)</span>
                </label>
                <input
                  type="number"
                  min={1} max={99}
                  value={templateSetup.templatePriority || 1}
                  onChange={e => onTemplateSetupChange?.('templatePriority', parseInt(e.target.value) || 1)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid="template-priority-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    // ── Normal listing mode: only show for new listings (no existing ID) ──
    if (formData.id) {
      return null;
    }
    
    return (
      <Card className="border-dashed border-2 border-primary/30 bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            {cardConfig.title || 'Quick Start from Template'}
          </CardTitle>
          {cardConfig.description && (
            <CardDescription>{cardConfig.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <MasterCatalogSelector
            productType={schema.listingType}
            category={formData[schema.categoryField] || formData.category || ''}
            subcategory={formData[schema.subcategoryField] || formData.subcategory || ''}
            isOpen={showMasterCatalogModal}
            onClose={() => setShowMasterCatalogModal(false)}
            onSelectTemplate={(templateData) => {
              // Apply template data to form (including hasVariants auto-enable)
              Object.entries(templateData).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                  handleChange(key, value);
                }
              });
              setShowMasterCatalogModal(false);
            }}
          />
          <Button 
            type="button"
            variant="outline" 
            className="w-full"
            onClick={() => setShowMasterCatalogModal(true)}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Browse Templates to Auto-fill Form
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  // Handle CategorySpecificAttributes component (for rentals with subcategory-based fields)
  if (cardConfig.component === 'CategorySpecificAttributes') {
    const subcategoryValue = formData[schema.subcategoryField] || formData.subcategory;
    return (
      <CategorySpecificAttributes
        schema={schema}
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        subcategory={subcategoryValue}
      />
    );
  }
  
  // Render standard field-based card with optional collapsible support
  const fields = cardConfig.fields || [];
  
  // Special handling for categorySelection card - render category/subcategory dropdowns with schema categories
  if (cardId === 'categorySelection' && schema.categories) {
    const schemaCategories = schema.categories;
    const categoryField = schema.categoryField || 'category';
    const subcategoryField = schema.subcategoryField || 'subcategory';
    const selectedCategory = formData[categoryField];
    
    // Get subcategories for selected category
    const categoryConfig = selectedCategory ? schemaCategories[selectedCategory] : null;
    const subcategoriesForCategory = categoryConfig?.subcategories || {};
    const subcategoryOptions = Object.entries(subcategoriesForCategory).map(([key, val]) => ({
      value: key,
      label: val.label || key
    }));
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{cardConfig.title}</CardTitle>
          {cardConfig.description && (
            <CardDescription>{cardConfig.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Category Select */}
          <div className="space-y-2">
            <Label className={`text-sm font-medium ${errors[categoryField] ? 'text-red-600' : ''}`}>
              Rental Category
              <span className="text-red-500 ml-1">*</span>
            </Label>
            <Select
              value={selectedCategory}
              onValueChange={(val) => {
                handleChange(categoryField, val);
                // Clear subcategory when category changes
                handleChange(subcategoryField, '');
              }}
            >
              <SelectTrigger 
                className={errors[categoryField] ? 'border-red-500' : ''} 
                data-testid="field-category"
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(schemaCategories).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>
                    {cat.label || key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors[categoryField] && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />{errors[categoryField]}
              </p>
            )}
          </div>
          
          {/* Subcategory Select - only show if category is selected */}
          {selectedCategory && subcategoryOptions.length > 0 && (
            <div className="space-y-2">
              <Label className={`text-sm font-medium ${errors[subcategoryField] ? 'text-red-600' : ''}`}>
                Rental Type
                <span className="text-red-500 ml-1">*</span>
              </Label>
              <Select
                value={formData[subcategoryField]}
                onValueChange={(val) => handleChange(subcategoryField, val)}
              >
                <SelectTrigger 
                  className={errors[subcategoryField] ? 'border-red-500' : ''} 
                  data-testid="field-subcategory"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {subcategoryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors[subcategoryField] && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{errors[subcategoryField]}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }
  
  // For collapsible cards
  if (isCollapsible) {
    // If this collapsible card is purely a ServiceableAreaCard, render standalone (no outer card)
    if (cardConfig.hasServiceableArea && (!fields || fields.length === 0)) {
      return (
        <ServiceableAreaCard
          formData={formData}
          handleChange={handleChange}
          kycTier={user?.vendor_profile?.business_details?.tax_compliance_tier || user?.compliance_tier || user?.kycTier}
          vendorState={user?.vendor_profile?.business_details?.state || user?.vendor_profile?.business_details?.registered_state || formData.vendorState}
          listingType={schema?.listingType}
        />
      );
    }
    return (
      <Card className={`transition-all duration-200 ${isCollapsed ? 'border-dashed' : ''}`}>
        <CardHeader 
          className={`py-3 cursor-pointer select-none ${isSidebar ? 'pb-3' : ''}`}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={isSidebar ? 'text-base' : 'text-lg'}>
                {cardConfig.title}
              </CardTitle>
              {cardConfig.description && isCollapsed && (
                <p className="text-xs text-gray-500 mt-0.5">{cardConfig.description}</p>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {!isCollapsed && (
          <CardContent className="space-y-4 pt-0">
            {/* Render fields with inline support */}
            {renderFieldsWithInlineSupport(fields, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema, insertBeforeTagInput)}
            
            {/* GST Auto-Suggestion for pricing cards */}
            {cardConfig.hasGstSuggestion && (
              <GstAutoSuggestionWrapper
                schema={schema}
                formData={formData}
                handleChange={handleChange}
              />
            )}
            
            {/* Serviceable Area Card - also in collapsible path */}
            {cardConfig.hasServiceableArea && (
              <div className="mt-6 pt-6 border-t">
                <ServiceableAreaCard
                  formData={formData}
                  handleChange={handleChange}
                  kycTier={user?.vendor_profile?.business_details?.tax_compliance_tier || user?.compliance_tier || user?.kycTier}
                  vendorState={user?.vendor_profile?.business_details?.state || user?.vendor_profile?.business_details?.registered_state || formData.vendorState}
                  listingType={schema?.listingType}
                />
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  }
  
  // If this card is purely a ServiceableAreaCard container, render it standalone
  if (cardConfig.hasServiceableArea && (!fields || fields.length === 0)) {
    return (
      <ServiceableAreaCard
        formData={formData}
        handleChange={handleChange}
        kycTier={user?.vendor_profile?.business_details?.tax_compliance_tier || user?.compliance_tier || user?.kycTier}
        vendorState={user?.vendor_profile?.business_details?.state || user?.vendor_profile?.business_details?.registered_state || formData.vendorState}
        listingType={schema?.listingType}
      />
    );
  }

  return (
    <Card>
      <CardHeader className={isSidebar ? 'pb-3' : ''}>
        <CardTitle className={isSidebar ? 'text-base' : 'text-lg'}>
          {cardConfig.title}
        </CardTitle>
        {cardConfig.description && !isSidebar && (
          <CardDescription>
            {formData.hasVariants && cardConfig.variantDescription 
              ? cardConfig.variantDescription 
              : cardConfig.description}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Info box for special cards */}
        {cardConfig.hasGstSuggestion && formData.chargeTax && (
          <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5" />
            <p className="text-sm text-blue-700">
              Enter your HSN/SAC code to ensure correct tax calculations.
            </p>
          </div>
        )}
        
        {/* KYC Warning for booking type - Bypass for admins */}
        {cardConfig.showKycStatus && cardConfig.kycWarning && formData.booking_type === 'booking' && !isAdmin && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">{cardConfig.kycWarning.title}</p>
                <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                  {cardConfig.kycWarning.steps.map((step, idx) => (
                    <li key={step.id || step.name || `${step.title || "step"}-${idx}`}>{step}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
        
        {/* Render fields with inline support */}
        {renderFieldsWithInlineSupport(fields, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema, insertBeforeTagInput)}
        
        {/* Volumetric shipping budget meter — Iter-210. Renders when card opts-in
            via "showVolumetricMeter": true (e.g. product Shipping card). Calls
            backend /api/volumetric/preview + /validate so vendors immediately
            see slab jumps before saving. Weight gets normalised to kg using the
            adjacent weightUnit field. */}
        {cardConfig.showVolumetricMeter && (() => {
          const w = parseFloat(formData.weight);
          const unit = (formData.weightUnit || 'kg').toLowerCase();
          // Normalise to kg for the backend
          const weightKg =
            !w || isNaN(w) ? 0 :
            unit === 'g'  ? w / 1000 :
            unit === 'lb' ? w * 0.453592 :
            unit === 'oz' ? w * 0.0283495 :
                            w; // 'kg' or unknown defaults to kg
          return (
            <VolumetricBudgetMeter
              length={parseFloat(formData.length) || 0}
              width={parseFloat(formData.width) || 0}
              height={parseFloat(formData.height) || 0}
              weight={weightKg}
              mode="listing"
              dataTestIdPrefix="listing-volumetric-meter"
            />
          );
        })()}
        
        {/* GST Auto-Suggestion for pricing cards */}
        {cardConfig.hasGstSuggestion && (
          <GstAutoSuggestionWrapper
            schema={schema}
            formData={formData}
            handleChange={handleChange}
          />
        )}
        
        {/* Serviceable Area Card - for location cards with geo-fencing */}
        {cardConfig.hasServiceableArea && (
            <ServiceableAreaCard
              formData={formData}
              handleChange={handleChange}
              kycTier={user?.vendor_profile?.business_details?.tax_compliance_tier || user?.compliance_tier || user?.kycTier}
              vendorState={user?.vendor_profile?.business_details?.state || user?.vendor_profile?.business_details?.registered_state || formData.vendorState}
              listingType={schema?.listingType}
            />
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Renders fields with support for inline (side-by-side) layout
 * On mobile, inline fields stack vertically
 */
const renderFieldsWithInlineSupport = (fields, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema = null, insertBeforeTagInput = null) => {
  const result = [];
  let i = 0;
  
  while (i < fields.length) {
    const field = fields[i];
    
    // Insert custom content before tag-input fields
    if (insertBeforeTagInput && field.type === 'tag-input') {
      result.push(
        <React.Fragment key="__filter-attrs-insert">
          {insertBeforeTagInput}
        </React.Fragment>
      );
    }
    
    // Check if this field and the next should be rendered inline
    if (field.inline && field.width === 'half' && i + 1 < fields.length) {
      const nextField = fields[i + 1];
      if (nextField.inline && nextField.width === 'half') {
        // Render both fields in a responsive flex row (stacks on mobile)
        result.push(
          <div key={`inline-${field.name}-${nextField.name}`} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              {renderField(field, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema)}
            </div>
            <div className="flex-1 min-w-0">
              {renderField(nextField, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema)}
            </div>
          </div>
        );
        i += 2;
        continue;
      }
    }
    
    // Render single field normally
    result.push(
      <React.Fragment key={field.name}>
        {renderField(field, formData, errors, handleChange, categories, subcategories, isSidebar, user, schema)}
      </React.Fragment>
    );
    i++;
  }
  
  return result;
};

/**
 * Wrapper component for GstAutoSuggestion to handle category ID lookup
 */
const GstAutoSuggestionWrapper = ({ schema, formData, handleChange }) => {
  const categoryType = schema.categoryType || schema.listingType;
  const { getCategoryByName, getSubcategoryByName } = useCategories(categoryType);
  
  const categoryField = schema.categoryField || 'category';
  const subcategoryField = schema.subcategoryField || 'subcategory';
  
  const categoryName = formData[categoryField];
  const subcategoryName = formData[subcategoryField];
  
  // Get category and subcategory IDs
  const category = getCategoryByName?.(categoryName);
  const subcategory = getSubcategoryByName?.(categoryName, subcategoryName);
  
  // Determine listing type for HSN/SAC code type
  const listingType = schema.listingType === 'product' ? 'product' : 'service';
  
  // Get current tax rate and HSN code from form data
  const currentTaxRate = formData.taxRate || '';
  const currentHsnCode = formData.hsn_sac_code || formData.hsnCode || '';
  
  // Get price from form data (handle different field names + multi-key rental/subscription writes)
  const price =
    formData.price
    || formData.dailyRate || formData.weeklyRate || formData.monthlyRate
    || formData.daily_rate || formData.weekly_rate || formData.monthly_rate
    || formData.unit_price || formData.monthlyPrice || formData.monthly_price
    || formData.basePlanPrice || formData.base_plan_price
    || formData.ticketPrice || formData.ticket_price
    || formData.hourlyRate || formData.pricePerUnit
    || 0;
  
  return (
    <GstAutoSuggestion
      categoryId={category?.id}
      subcategoryId={subcategory?.id}
      subcategoryName={subcategoryName}
      price={price}
      currentTaxRate={currentTaxRate}
      onTaxRateChange={(rate) => handleChange('taxRate', rate)}
      currentHsnCode={currentHsnCode}
      onHsnCodeChange={(code) => handleChange(listingType === 'product' ? 'hsnCode' : 'hsn_sac_code', code)}
      chargeTax={formData.chargeTax}
      listingType={listingType}
    />
  );
};

export default SchemaCardRenderer;
